import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteComponentConfirmationDialog from "../DeleteComponentConfirmationDialog";
import { RecoverComponentDialog } from "../RecoverComponentDialog";
import { TrashComponentDialog } from "../TrashComponentDialog";
import { useAuth0 } from "@auth0/auth0-react";
import { deleteComponent, deleteSharedConnectors } from "api/ComponentsApi";
import { ShareConnectorDialog } from "layouts/pages/library/connector/components/shareConnector";
import { UnShareConnectorDialog } from "layouts/pages/library/connector/components/unshareConnector";
import { updateUserComponentGroup } from "redux/actions/flowDesigner/componentGroupsActionCreator";
import { updateComponent } from "redux/actions/flowDesigner/cxfComponentsActionCreator";
import { useConnectorPermissions } from "hooks/useConnectorPermissions";
import { RegisterComponentDialog } from "layouts/Flow Designer/components/RegisterComponentDialog";

const ComponentMenu = ({ component, isVertical = false, deleteCallback, isReadOnlyAccess }) => {
   const [connectorMenu, setConnectorMenu] = useState(null);
   const [openShareComponentDialog, setOpenShareComponentDialog] = useState(false);
   const [openViewConnectorPropertiesDialog, setOpenViewConnectorPropertiesDialog] = useState(false);
   const [openUnShareComponentDialog, setOpenUnShareComponentDialog] = useState(false);
   const [openDeleteComponentConfirmationDialog, setOpenDeleteComponentConfirmationDialog] = useState(false);
   const [openTrashComponentDialog, setOpenTrashComponentDialog] = useState(false);
   const [openRecoverComponentDialog, setOpenRecoverComponentDialog] = useState(false);
   const [actionType, setActionType] = useState("");
   const { user } = useAuth0();
   const dispatch = useDispatch();
   const { userComponentGroups } = useSelector((store) => store?.designer?.componentGroups);
   const { savingComponentFlagSuccess } = useSelector((store) => store?.designer?.cxfComponents);
   const { sharedConnectors } = useSelector((store) => store?.portal?.connectors);
   const { users } = useSelector((store) => store?.portal?.users);
   const navigate = useNavigate();

   const { canEdit, canDelete, canShare, canOpenMenu } = useConnectorPermissions(component);

   const handleOpenConnectorMenu = (event) => {
      setConnectorMenu(event.currentTarget);
   };

   const handleCloseConnectorMenu = () => {
      setConnectorMenu(null);
   };

   const handleOpenShareComponentDialog = () => {
      setOpenShareComponentDialog(true);
   };

   const handleOpenUnShareComponentDialog = () => {
      setOpenUnShareComponentDialog(true);
   };

   const handleCloseShareComponentDialog = () => {
      setOpenShareComponentDialog(false);
      handleCloseConnectorMenu();
   };

   const handleCloseUnShareComponentDialog = () => {
      setOpenUnShareComponentDialog(false);
      handleCloseConnectorMenu();
   };

   const handleCloseConnectorPropertiesDialog = () => {
      setOpenViewConnectorPropertiesDialog(false);
      handleCloseConnectorMenu();
   };

   const handleOpenTrashComponentDialog = () => {
      handleCloseConnectorMenu();
      setOpenTrashComponentDialog(true);
   };

   const handleCloseTrashComponentDialog = () => {
      setOpenTrashComponentDialog(false);
   };

   const handleOpenRecoverComponentDialog = () => {
      handleCloseConnectorMenu();
      setOpenRecoverComponentDialog(true);
   };

   const handleCloseRecoverComponentDialog = () => {
      setOpenRecoverComponentDialog(false);
   };

   const handleViewConnector = () => {
      handleCloseConnectorMenu();
      navigate(`/connector/${component.id}`);
   };

   const handleOpenDeleteComponentDialog = () => {
      handleCloseConnectorMenu();

      setOpenDeleteComponentConfirmationDialog(true);
   };

   const handleCloseDeleteComponentDialog = () => {
      setOpenDeleteComponentConfirmationDialog(false);
   };

   const handleDeleteComponent = async () => {
      let deleteComponentResponse;

      try {
         deleteComponentResponse = await deleteComponent(user?.stripeId, component?.id);
      } catch {
         toast.error("Failed to delete connector");
         return false;
      }

      const foundComponentGroup = userComponentGroups?.find((group) => group?.component_groups?.find((groupComponent) => groupComponent.component_id == component?.id));

      if (foundComponentGroup) {
         const { components, created_date_time, id, ...restFoundComponentGroup } = foundComponentGroup;

         dispatch(
            updateUserComponentGroup(user?.stripeId, id, {
               ...restFoundComponentGroup,
               component_groups: restFoundComponentGroup.component_groups.filter((groupComponent) => groupComponent.component_id !== component.id)
            })
         );
      }

      if (deleteComponentResponse?.isSuccessful) {
         await deleteSharedConnectors(
            user?.stripeId,
            sharedConnectors.filter((shared) => shared.connector_id === component.id).map((shared) => shared.id)
         );
         toast.success("Delete connector successful");
         if (deleteCallback) deleteCallback();
      } else {
         toast.error(deleteComponentResponse?.message);
         return false;
      }

      return true;
   };

   const handleTrashComponent = async () => {
      dispatch(updateComponent(user, component.id, { status: "deleted" }));
      setActionType("trash");

      return true;
   };

   const handleRecoverComponent = () => {
      dispatch(updateComponent(user, component.id, { status: "active" }));
      setActionType("recover");
   };

   const canShareConnector = () => {
      return users.length - 1 !== sharedConnectors.filter(({ connector_id }) => connector_id === component.id).length;
   };

   const hasSharedUsers = () => {
      return sharedConnectors.some(({ connector_id }) => connector_id === component.id);
   };

   const handleDeletePermanentlyCallback = () => {
      handleCloseTrashComponentDialog();
      handleOpenDeleteComponentDialog();
   };

   useEffect(() => {
      if (!savingComponentFlagSuccess || !actionType) return;

      toast.success(`Connector successfully ${actionType === "trash" ? "trashed" : "recovered"}`);
      setActionType("");
   }, [savingComponentFlagSuccess, actionType]);

   return (
      <>
         {canOpenMenu && !isReadOnlyAccess && (
            <Tooltip title="More Actions" placement="top">
               <Icon fontSize="default" onClick={handleOpenConnectorMenu} sx={{ cursor: "pointer" }}>
                  {isVertical ? "more_vertical_icon" : "more_horiz_icon"}
               </Icon>
            </Tooltip>
         )}
         <Menu
            anchorEl={connectorMenu}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={Boolean(connectorMenu)}
            onClose={handleCloseConnectorMenu}
            keepMounted
         >
            <MenuItem onClick={handleViewConnector}>View</MenuItem>
            {canShare && hasSharedUsers() && <MenuItem onClick={handleOpenUnShareComponentDialog}>Unshare</MenuItem>}

            {canShare && canShareConnector() && <MenuItem onClick={handleOpenShareComponentDialog}>Share</MenuItem>}

            {canDelete && component.status !== "deleted" && <MenuItem onClick={handleOpenTrashComponentDialog}>Delete</MenuItem>}

            {canEdit && component.status === "deleted" && <MenuItem onClick={handleOpenRecoverComponentDialog}>Recover</MenuItem>}

            {canDelete && component?.status === "deleted" && <MenuItem onClick={handleOpenDeleteComponentDialog}>Delete Permanently</MenuItem>}
         </Menu>
         <DeleteComponentConfirmationDialog open={openDeleteComponentConfirmationDialog} onClose={handleCloseDeleteComponentDialog} onAccept={handleDeleteComponent} component={component} />
         <TrashComponentDialog
            open={openTrashComponentDialog}
            onClose={handleCloseTrashComponentDialog}
            onAccept={handleTrashComponent}
            component={component}
            deletePermanentlyCallback={handleDeletePermanentlyCallback}
         />
         <RecoverComponentDialog open={openRecoverComponentDialog} onClose={handleCloseRecoverComponentDialog} onAccept={handleRecoverComponent} />
         <ShareConnectorDialog component={component} open={openShareComponentDialog} onClose={handleCloseShareComponentDialog} />
         <UnShareConnectorDialog component={component} open={openUnShareComponentDialog} onClose={handleCloseUnShareComponentDialog} />
         <RegisterComponentDialog component={component} open={openViewConnectorPropertiesDialog} isReadOnly onClose={handleCloseConnectorPropertiesDialog} />
      </>
   );
};

export default ComponentMenu;
